/**
 * Ported from three/examples/jsm/lines/LineMaterial.js (MIT License).
 * Source: https://github.com/mrdoob/three.js/blob/master/examples/jsm/lines/
 *
 * Adapted to TypeScript:
 *   - Strict typing on constructor params and internal state
 *   - GLSL shader extracted to a separate `.glsl.ts` template literal module
 *   - `onUploadCallback` defensive null-ing (Opt 1a) added on instance buffers
 */

export const FRAGMENT_SHADER = /* glsl */ `
			uniform vec3 diffuse;
			uniform float opacity;
			uniform float linewidth;

			#ifdef USE_DASH

				uniform float dashOffset;
				uniform float dashSize;
				uniform float gapSize;

				#ifdef USE_DASH_TEXTURE

					// PR-LT3 variant A (A1) — full multi-element pattern. The
					// sampler carries one texel per pattern element (see
					// linetypePatternTexture.ts): R+G = 16-bit cumulative END
					// offset (÷65535), B = kind (gap 0 / dot 0.5 / dash ~1),
					// A = element's own normalized length. Walked with a
					// constant-bounded loop + texture2D + computed UV so the
					// path stays WebGL1-safe (no texelFetch).
					uniform sampler2D dashPatternTex;
					uniform float dashPatternLength;
					uniform float dashElementCount;

					// Constant loop bound — must match MAX_DASH_ELEMENTS in
					// linetypePatternTexture.ts. Real linetypes top out at 6.
					#define MAX_DASH_ELEMENTS 16

				#endif

			#endif

			varying float vLineDistance;

			#ifdef WORLD_UNITS

				varying vec4 worldPos;
				varying vec3 worldStart;
				varying vec3 worldEnd;

				#ifdef USE_DASH

					varying vec2 vUv;

				#endif

			#else

				varying vec2 vUv;

			#endif

			#include <common>
			#include <color_pars_fragment>
			#include <fog_pars_fragment>
			#include <logdepthbuf_pars_fragment>
			#include <clipping_planes_pars_fragment>

			vec2 closestLineToLine(vec3 p1, vec3 p2, vec3 p3, vec3 p4) {

				float mua;
				float mub;

				vec3 p13 = p1 - p3;
				vec3 p43 = p4 - p3;

				vec3 p21 = p2 - p1;

				float d1343 = dot( p13, p43 );
				float d4321 = dot( p43, p21 );
				float d1321 = dot( p13, p21 );
				float d4343 = dot( p43, p43 );
				float d2121 = dot( p21, p21 );

				float denom = d2121 * d4343 - d4321 * d4321;

				float numer = d1343 * d4321 - d1321 * d4343;

				mua = numer / denom;
				mua = clamp( mua, 0.0, 1.0 );
				mub = ( d1343 + d4321 * ( mua ) ) / d4343;
				mub = clamp( mub, 0.0, 1.0 );

				return vec2( mua, mub );

			}

			void main() {

				float alpha = opacity;
				vec4 diffuseColor = vec4( diffuse, alpha );

				#include <clipping_planes_fragment>

				#ifdef USE_DASH

					if ( vUv.y < - 1.0 || vUv.y > 1.0 ) discard; // discard endcaps

					#ifdef USE_DASH_TEXTURE

						// A1 — multi-element pattern walk. p = normalized
						// position within the repeat period [0,1).
						float p = mod( vLineDistance + dashOffset, dashPatternLength ) / dashPatternLength;

						// Half-width of a dot's round mark, in normalized period
						// units. AutoCAD draws a dot as a zero-length round cap;
						// we approximate it with a tiny visible slice centered on
						// the dot's position so DOT/DASHDOT show points, not gaps.
						float dotHalf = 0.012;

						bool keep = false;   // becomes true when p lands in a dash

						// FIX-5 (Codex P2) — dot mark MUST span BOTH sides of its
						// boundary. A dot has zero length, so its cumulative-end
						// (cumEnd) coincides with the PRECEDING gap's cumEnd.
						// In the linear gap/dash walk the enclosing gap (processed
						// first) discards every fragment with p < cumEnd before
						// the loop reaches the dot texel, so the dot only ever
						// rendered on the side AFTER its boundary (and Dot's
						// wraparound half at p~1 / p~0 was never drawn). Pre-scan
						// for any dot whose boundary is within dotHalf of p
						// — measuring the SHORTEST distance around the period (so a
						// dot at cumEnd~1.0 also lights fragments at p~0.0) — and
						// keep those fragments BEFORE the gap can discard them. This
						// reads the dot as a centered round mark on its boundary.
						bool dotHit = false;
						for ( int i = 0; i < MAX_DASH_ELEMENTS; i ++ ) {

							if ( i >= int( dashElementCount + 0.5 ) ) break;

							float u = ( float( i ) + 0.5 ) / dashElementCount;
							vec4 texel = texture2D( dashPatternTex, vec2( u, 0.5 ) );
							float kind = texel.b;

							// Only dots (B≈0.5) define a centered round mark.
							if ( kind > 0.25 && kind < 0.75 ) {
								float hi = floor( texel.r * 255.0 + 0.5 );
								float lo = floor( texel.g * 255.0 + 0.5 );
								float cumEnd = ( hi * 256.0 + lo ) / 65535.0;

								// Shortest signed distance on the unit-period ring
								// (wraparound: a dot near 1.0 covers p near 0.0).
								float d = abs( p - cumEnd );
								d = min( d, 1.0 - d );
								if ( d < dotHalf ) {
									dotHit = true;
									break;
								}
							}
						}

						if ( dotHit ) {
							keep = true;
						} else {
							// Linear gap/dash walk for the non-dot fragments.
							for ( int i = 0; i < MAX_DASH_ELEMENTS; i ++ ) {

								if ( i >= int( dashElementCount + 0.5 ) ) break;

								float u = ( float( i ) + 0.5 ) / dashElementCount;
								vec4 texel = texture2D( dashPatternTex, vec2( u, 0.5 ) );

								// Reconstruct the 16-bit cumulative end offset.
								float hi = floor( texel.r * 255.0 + 0.5 );
								float lo = floor( texel.g * 255.0 + 0.5 );
								float cumEnd = ( hi * 256.0 + lo ) / 65535.0;

								// kind: gap (B≈0), dot (B≈0.5), dash (B≈1).
								float kind = texel.b;

								// Dots occupy no [prev,cumEnd] span — already
								// handled by the pre-scan above; skip them here.
								if ( kind > 0.25 && kind < 0.75 ) {
									continue;
								}

								if ( p < cumEnd ) {
									// First dash/gap element whose span contains p.
									keep = ( kind > 0.75 ); // dash keeps, gap discards
									break;
								}
							}
						}

						if ( ! keep ) discard;

					#else

						if ( mod( vLineDistance + dashOffset, dashSize + gapSize ) > dashSize ) discard;

					#endif

				#endif

				#ifdef WORLD_UNITS

					// Find the closest points on the view ray and the line segment
					vec3 rayEnd = normalize( worldPos.xyz ) * 1e5;
					vec3 lineDir = worldEnd - worldStart;
					vec2 params = closestLineToLine( worldStart, worldEnd, vec3( 0.0, 0.0, 0.0 ), rayEnd );

					vec3 p1 = worldStart + lineDir * params.x;
					vec3 p2 = rayEnd * params.y;
					vec3 delta = p1 - p2;
					float len = length( delta );
					float norm = len / linewidth;

					#ifndef USE_DASH

						#ifdef USE_ALPHA_TO_COVERAGE

							float dnorm = fwidth( norm );
							alpha = 1.0 - smoothstep( 0.5 - dnorm, 0.5 + dnorm, norm );

						#else

							if ( norm > 0.5 ) {

								discard;

							}

						#endif

					#endif

				#else

					#ifdef USE_ALPHA_TO_COVERAGE

						// artifacts appear on some hardware if a derivative is taken within a conditional
						float a = vUv.x;
						float b = ( vUv.y > 0.0 ) ? vUv.y - 1.0 : vUv.y + 1.0;
						float len2 = a * a + b * b;
						float dlen = fwidth( len2 );

						if ( abs( vUv.y ) > 1.0 ) {

							alpha = 1.0 - smoothstep( 1.0 - dlen, 1.0 + dlen, len2 );

						}

					#else

						if ( abs( vUv.y ) > 1.0 ) {

							float a = vUv.x;
							float b = ( vUv.y > 0.0 ) ? vUv.y - 1.0 : vUv.y + 1.0;
							float len2 = a * a + b * b;

							if ( len2 > 1.0 ) discard;

						}

					#endif

				#endif

				#include <logdepthbuf_fragment>
				#include <color_fragment>

				gl_FragColor = vec4( diffuseColor.rgb, alpha );

				#include <tonemapping_fragment>
				#include <colorspace_fragment>
				#include <fog_fragment>
				#include <premultiplied_alpha_fragment>

			}
			`;
